import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck, FaPlus, FaMinus } from 'react-icons/fa';

interface AccordionItem {
  title: string;
  content: string;
}

const industryVerticals = [
  { title: 'Stablecoin\nIssuers', link: '/market-segments/stablecoin-issuers' },
  { title: 'RWA & Asset-backed Token\nIssuers', link: '/market-segments/rwa-token-issuers' },
  { title: 'Exchanges,\nCustodians, &\nBanks', link: '/market-segments/exchanges-custodians' },
  { title: 'Exchange-Traded\nProducts', link: '#' },
  { title: 'DAOs, Network\nStates & On-Chain\nOrganizations', link: '/market-segments/daos' },
  { title: 'Governments &\nNation States', link: '#' },
  { title: 'Miners & Stakers', link: '#' },
  { title: 'Payment\nProcessors &\nFinTechs', link: '/market-segments/payment-processors' },
];

const accordionData: AccordionItem[] = [
  {
    title: 'Provide Monthly Transparency Reports to Customers, Regulators, and Counterparties',
    content: 'For stablecoins, RWAs, and asset-backed tokens, monthly and public attestation has become the industry standard.',
  },
  {
    title: 'Provide Investors with Demonstable Business Processes',
    content: 'Demonstrate the strength and transparency of your business processes to build investor confidence.',
  },
  {
    title: 'Fulfill Regulatory Requirements',
    content: 'Ensure compliance with stringent regulatory requirements promulgated by bodies like the NYDFS while strengthening operational integrity.',
  },
  {
    title: 'Support Fundraising & Due Diligence',
    content: 'Strengthen your fundraising efforts and simplify due diligence with trusted an attestation from an independent 3rd party.',
  },
  {
    title: 'Increase Trust with Financial Counterparties',
    content: 'Build confidence and strengthen relationships with financial counterparties through reliable attestation reports.',
  },
  {
    title: 'Fulfill Onboarding & KYB Requirements',
    content: 'Streamline due diligence, onboarding and Know Your Business (KYB) requirements with ease.',
  },
];

export default function Attestations() {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [currentHero] = useState(0);

  const heroVariants = [
    {
      subtitle: 'RWA Token Issuers',
      description: 'Independent examinations and agreed-upon procedures attestations for crypto-specific use cases.',
    },
    {
      subtitle: 'Payment Processors',
      description: 'Independent examinations and agreed-upon procedures attestations for crypto-specific use cases.',
    },
  ];

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#0A0F29] text-white py-20 md:py-32 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/cpa.jpg"
            alt="CPA Services Background"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Attestation Services for{' '}
              <span className="text-blue-500">
                {heroVariants[currentHero].subtitle}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
              {heroVariants[currentHero].description}
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <FaCheck className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-300">
                  Complete examinations and AUPs with crypto-specific criteria and procedures for custom purposes.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <FaCheck className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-300">
                  Fulfill regulatory obligations with NYDFS and other stringent jurisdictions.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <FaCheck className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-300">
                  Support expansion efforts across DeFi, CeFi, and TradFi with custom attestation reports.
                </p>
              </div>
            </div>

            <button className="bg-transparent border-2 border-blue-500 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-blue-500 transition-colors">
              TALK TO AN EXPERT
            </button>
          </div>
        </div>
      </section>

      {/* Industry Verticals Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-blue-600 mb-12">
            Independent Attestations for Industry Verticals
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industryVerticals.map((vertical, index) => (
              <Link
                key={index}
                to={vertical.link}
                className="bg-gray-50 p-8 rounded-lg text-center hover:shadow-xl transition-shadow duration-300 border border-gray-200 flex items-center justify-center min-h-[180px]"
              >
                <h3 className="text-xl font-bold text-gray-900 whitespace-pre-line">
                  {vertical.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Unlock Your Next Stage of Growth Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-blue-600 mb-12">
            Unlock Your Next Stage of Growth
          </h2>

          <div className="max-w-5xl mx-auto border-t border-gray-300">
            {accordionData.map((item, index) => (
              <div key={index} className="border-b border-gray-300">
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex items-center justify-between py-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 pr-4">
                    {item.title}
                  </h3>
                  <div className="flex-shrink-0">
                    {openAccordion === index ? (
                      <FaMinus className="w-6 h-6 text-gray-700" />
                    ) : (
                      <FaPlus className="w-6 h-6 text-gray-700" />
                    )}
                  </div>
                </button>
                {openAccordion === index && (
                  <div className="pb-6 pr-12">
                    <p className="text-lg text-gray-700">{item.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team, Tools, and Technical Expertise Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-600 leading-tight">
                The Team, Tools, and Technical Expertise to streamline your attestation process and enhance your credibility.
              </h2>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <FaCheck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-900">
                  <strong>Team:</strong> Our team of CPA's has 7+ years of digital asset industry experience and 10+ performing audits and attestations.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <FaCheck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-900">
                  <strong>Tools:</strong> By leveraging proprietary{' '}
                  <a href="#" className="text-blue-600 underline hover:text-blue-700">
                    crypto audit technology
                  </a>
                  , we verify balances, prove ownership of addresses, and inspect blockchain data in a fraction of the time of our competitors.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <FaCheck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <p className="text-lg text-gray-900">
                  <strong>Technical Expertise:</strong> Our team has the technical expertise to handle complex and unique reporting criteria and procedures.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-blue-600 mb-12">
            Don't Take Our Word for It
          </h2>

          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-shrink-0">
              <img
                src="https://via.placeholder.com/200"
                alt="Robert Kalkstein"
                className="w-48 h-48 rounded-full object-cover"
              />
            </div>

            <div>
              <p className="text-gray-700 mb-4">
                Robert Kalkstein, GMO-Z Trust Company, CFO
              </p>
              <blockquote className="text-2xl md:text-3xl font-bold text-gray-900 leading-relaxed">
                "The Audit Financier Team has the blockchain expertise paired with assurance prowess. They are a lean and nimble firm. Altogether, The Audit Financier Team is the best choice for assurance services in the blockchain industry."
              </blockquote>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}